/**
 * Data Layer - Response Handling
 * Handles API response processing and normalization
 */

import { AxiosResponse } from "axios";
import { PAGINATION } from "@/constants";
import type {
  ApiResponse,
  PaginatedResponse,
  PaginatedApiResponse,
} from "@/shared/core/types";

/**
 * Response handler for processing API responses
 */
export const responseHandler = {
  /**
   * Process a standard API response
   * @param response The Axios response
   * @returns Processed data from the response
   */
  process<T>(response: AxiosResponse<ApiResponse<T>>): T {
    // If the API doesn't follow the standard structure, handle it
    if (response.data.success === undefined) {
      return response.data as unknown as T;
    }

    // Check if the response indicates a failure
    if (response.data.success === false) {
      throw new Error(response.data.message || "errors.api.responseError");
    }

    // Return the data if everything is fine
    return response.data.response as T;
  },

  /**
   * Process a paginated API response
   * @param response The Axios response containing pagination
   * @returns Processed data with pagination metadata
   */
  processPaginated<T>(
    response: AxiosResponse<ApiResponse<T[]> | PaginatedApiResponse<T>>
  ): PaginatedApiResponse<T> {
    // Process the response to check for success
    if (response.data.success === false) {
      throw new Error(response.data.message || "errors.api.responseError");
    }

    // Check which response structure we're dealing with
    // If it's the newer format with response.data
    if (
      "response" in response.data &&
      response.data.response &&
      "data" in response.data.response
    ) {
      const paginatedResponse = response.data as PaginatedApiResponse<T>;
      const paginationData = paginatedResponse.response!;

      return {
        response: {
          data: paginationData.data,
          total: paginationData.total,
          per_page: paginationData.per_page,
          current_page: paginationData.current_page,
          last_page: paginationData.last_page,
          from: paginationData.from,
          to: paginationData.to,
          links: paginationData.links,
        },
        success: true,
        message: paginatedResponse.message,
      };
    }

    // Otherwise use the older format with meta.pagination
    const apiResponse = response.data as ApiResponse<T[]>;
    const data = this.process<T[]>(response as AxiosResponse<ApiResponse<T[]>>);

    // Extract pagination metadata
    const pagination = apiResponse.meta?.pagination || {
      total: 0,
      current_page: 1,
      per_page: 10,
      last_page: 1,
      from: 0,
      to: 0,
      links: {
        first: "",
        last: "",
        prev: null,
        next: null,
      },
    };

    // Return standardized response
    return {
      response: {
        data: data,
        total: pagination.total,
        per_page: pagination.per_page,
        current_page: pagination.current_page,
        last_page: pagination.last_page,
        from: pagination.from,
        to: pagination.to,
        links: pagination.links,
      },
      success: true,
      message: apiResponse.message,
    };
  },
};

/**
 * Legacy class export for backward compatibility
 */
export class ResponseHandler {
  static process<T>(response: AxiosResponse<ApiResponse<T>>): T {
    return responseHandler.process(response);
  }

  static processPaginated<T>(
    response: AxiosResponse<ApiResponse<T[]> | PaginatedApiResponse<T>>
  ): PaginatedApiResponse<T> {
    return responseHandler.processPaginated(response);
  }
}

export default responseHandler;
